import { readable, writable, derived } from 'svelte/store'

import { json } from 'd3-fetch'
import { extent } from 'd3-array'
import { interpolateSinebow } from 'd3-scale-chromatic'

const databaseFile = './data/database.json'

const stories = readable([], async function start (set) {
  const data = await json(databaseFile)
  set(data)
  return function stop () {}
})

const coordsRange = derived(stories, $stories => {
  const coords2d = $stories
    .map(s => [s.structure_coords_2d, s.style_coords_2d, s.topic_coords_2d])
    .flat()
  const coords3d = $stories
    .map(s => [s.structure_coords_3d, s.style_coords_3d, s.topic_coords_3d])
    .flat()
  const coordsSpread = $stories
    .map(s => [s.structure_coords_spread, [], []])
    .flat()
  return {
    '2d': {
      //original 2d coordinates
      x: extent(coords2d, d => d[0]),
      y: extent(coords2d, d => d[1]),
    },
    spread: {
      //spread-out coordinates, z represents centrality
      x: extent(coordsSpread, d => d[0]),
      y: extent(coordsSpread, d => d[1]),
      z: extent(coordsSpread, d => d[2]),
    },
  }
})

const clusterTypes = [
  { label: 'Style', prop: 'style_coords_spread' },
  { label: 'Structure', prop: 'structure_coords_spread' },
  { label: 'Topic', prop: 'topic_coords_spread' },
]

const selectedClusterType = writable(clusterTypes[0])

const genres = derived(stories, $stories =>
  $stories
    .map(s => s.genre)
    .filter((v, i, a) => a.indexOf(v) === i)
    .sort()
)

const selectedGenres = writable([])

const themes = derived(stories, $stories =>
  $stories
    .map(s => s.theme)
    .filter((v, i, a) => a.indexOf(v) === i)
    .sort()
)

const selectedThemes = writable([])

const countries = derived(stories, $stories =>
  $stories
    .map(s => s.country)
    .filter((v, i, a) => a.indexOf(v) === i)
    .sort()
)

const selectedCountries = writable([])

const hoveredObjects = writable([])

const selectedStory = writable([])

const filteredStories = derived(
  [stories, selectedThemes, selectedGenres, selectedCountries],
  ([$stories, $selectedThemes, $selectedGenres, $selectedCountries]) =>
    $stories.filter(
      s =>
        ($selectedThemes.length == 0 ||
          $selectedThemes.indexOf(s.theme) != -1) &&
        ($selectedGenres.length == 0 ||
          $selectedGenres.indexOf(s.genre) != -1) &&
        ($selectedCountries.length == 0 ||
          $selectedCountries.indexOf(s.country) != -1)
    )
)

const colorCriterium = writable('genre')

const colorScheme = derived(
  [stories, colorCriterium],
  ([$stories, $colorCriterium]) => {
    if (!$colorCriterium) return null
    //get unique values for selected criterium
    const uniqueValues = $stories
      .map(s => s[$colorCriterium])
      .filter((v, i, a) => a.indexOf(v) === i)
      .sort()

    let colorScheme = {}
    uniqueValues.forEach((v, index) => {
      colorScheme[v] = interpolateSinebow(index / uniqueValues.length)
    })
    return colorScheme
  }
)

const minChainPresencePct = writable(0)
const minChainLength = writable(0)
const gap = writable(40)

export {
  stories,
  coordsRange,
  clusterTypes,
  selectedClusterType,
  genres,
  selectedGenres,
  themes,
  selectedThemes,
  countries,
  selectedCountries,
  hoveredObjects,
  selectedStory,
  filteredStories,
  colorCriterium,
  colorScheme,
  minChainPresencePct,
  minChainLength,
  gap,
}
